package com.flowyride.core.domain.place

import com.flowyride.core.domain.favorite.FavoriteException
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Place
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * An address the rider named ("Maison", "Garage"): one tap to ride there.
 *
 * @param emoji shown on its chip and on the map instead of a plain dot; null: the dot.
 * @param color of its dot or of the ring around its emoji, 0xRRGGBB; null: the app's default.
 */
data class SavedPlace(
    val id: Long,
    val name: String,
    val detail: String?,
    val point: GeoPoint,
    val emoji: String? = null,
    val color: Int? = null,
) {
    fun toPlace() = Place(name, detail, point)
}

data class NewSavedPlace(
    val name: String,
    val detail: String?,
    val point: GeoPoint,
    val emoji: String? = null,
    val color: Int? = null,
)

/** What the rider can change on a saved address: everything but where it is. */
data class SavedPlaceEdit(val name: String, val emoji: String?, val color: Int?)

/** Validation rules shared by the apps and the server (authoritative). */
object SavedPlaceRules {
    const val NAME_MAX = 40
    const val DETAIL_MAX = 200
    const val MAX_PER_ACCOUNT = 50

    /** UTF-16 units: room for flags and joined emoji ("👨‍👩‍👧"), not for a sentence. */
    const val EMOJI_MAX = 16

    /** @return the first broken rule, or null when [place] can be saved. */
    fun validate(place: NewSavedPlace): String? = when {
        (place.detail?.length ?: 0) > DETAIL_MAX -> "Detail: at most $DETAIL_MAX characters"
        else -> validate(SavedPlaceEdit(place.name, place.emoji, place.color))
    }

    /** @return the first broken rule, or null when [edit] can be applied. */
    fun validate(edit: SavedPlaceEdit): String? = when {
        edit.name.isBlank() -> "Name required"
        edit.name.length > NAME_MAX -> "Name: at most $NAME_MAX characters"
        edit.emoji != null && (edit.emoji.isBlank() || edit.emoji.length > EMOJI_MAX) -> "Emoji: 1-$EMOJI_MAX characters"
        edit.color != null && edit.color !in 0..0xFFFFFF -> "Color: 0xRRGGBB"
        else -> null
    }
}

/** Addresses saved on the rider's account. Failures are [FavoriteException]s, like the saved rides. */
interface SavedPlaceRepository {
    /** Oldest first: the first ones saved ("Maison") stay in front. */
    suspend fun list(): List<SavedPlace>

    suspend fun save(place: NewSavedPlace): SavedPlace

    suspend fun update(id: Long, edit: SavedPlaceEdit): SavedPlace

    suspend fun delete(id: Long)
}

/**
 * The account's addresses, shared by every screen that shows them (planner chips and map, settings):
 * a change made on one shows on the others. Failures are thrown to the caller, the list stays as it was.
 */
class SavedPlaces(private val repository: SavedPlaceRepository) {
    private val _places = MutableStateFlow<List<SavedPlace>>(emptyList())
    val places: StateFlow<List<SavedPlace>> = _places.asStateFlow()

    suspend fun refresh() {
        _places.value = repository.list()
    }

    suspend fun save(place: NewSavedPlace): SavedPlace =
        repository.save(place).also { saved -> _places.update { it + saved } }

    suspend fun update(id: Long, edit: SavedPlaceEdit): SavedPlace =
        repository.update(id, edit).also { updated -> _places.update { list -> list.map { if (it.id == id) updated else it } } }

    suspend fun delete(id: Long) {
        repository.delete(id)
        _places.update { list -> list.filterNot { it.id == id } }
    }
}
